import type { ProductForClient } from "@/lib/shared/types/interfaces";
import { getProductPrice } from "@/lib/shared/utils/calculatePrice";

/**
 * Lowest unit price in shop base currency for one product (same rule as cart: on-sale price when set).
 */
export function minVariantUnitPriceBase(product: ProductForClient): number {
  let best = Number.POSITIVE_INFINITY;
  for (const row of product.variants) {
    const unit = getProductPrice(product, row.variant, 1);
    if (unit < best) best = unit;
  }
  return best === Number.POSITIVE_INFINITY ? 0 : best;
}

/**
 * Unit price for the product's first variant only (base currency). Used for gift-set bundle reference
 * totals so admin discount-ratio math matches storefront "was" pricing.
 */
export function firstVariantUnitPriceBase(product: ProductForClient): number {
  const row = product.variants[0];
  if (!row) return 0;
  return getProductPrice(product, row.variant, 1);
}

/** Sum of per-member minimum prices (base currency). */
export function sumGiftSetMembersMinComparisonBase(
  products: { product: ProductForClient }[],
): number {
  const raw = products.reduce((sum, line) => sum + minVariantUnitPriceBase(line.product), 0);
  return Number(raw.toFixed(2));
}

/** Sum of each member’s first-variant price (base currency) — bundle comparison / discount reference. */
export function sumGiftSetMembersFirstVariantComparisonBase(
  products: { product: ProductForClient }[],
): number {
  const raw = products.reduce((sum, line) => sum + firstVariantUnitPriceBase(line.product), 0);
  return Number(raw.toFixed(2));
}

/**
 * Bundle line total from a member subtotal and discount ratio (same currency as `membersSum`).
 * `discountRatio` is the fraction off (0.2 → pay 80% of the sum). Clamped to [0, 1].
 */
export function bundlePriceFromMembersAndDiscountRatio(
  membersSum: number,
  discountRatio: number,
): number {
  const r = Number.isFinite(discountRatio) ? Math.min(1, Math.max(0, discountRatio)) : 0;
  return Number((membersSum * (1 - r)).toFixed(2));
}

/** Sum of chosen variant prices (base currency) — detail page when variants are selected. */
export function sumGiftSetMembersWithVariantsBase(
  products: { id: string; product: ProductForClient }[],
  variantByLineId: Record<string, string>,
): number {
  let sum = 0;
  for (const line of products) {
    const vid = variantByLineId[line.id];
    const row =
      (vid ? line.product.variants.find((v) => String(v.variant.id) === vid) : undefined) ??
      line.product.variants[0];
    if (!row) continue;
    sum += getProductPrice(line.product, row.variant, 1);
  }
  return Number(sum.toFixed(2));
}
